import React, { useState, useMemo, useEffect } from "react";
import BreadCrumb from "../../components/BreadCrumb";
import { GUIDE_STEPS } from "../../utils/internalPageData";
import { FaCircle } from "react-icons/fa6";
import { pushToDataLayer } from "../../utils/gtm";

const MoneyTransferGuide = () => {
  const [activeStep, setActiveStep] = useState(0);
  const {
    title,
    content,
    bullets,
    bank_transfer,
    credit_cards,
    digital_wallets,
  } = useMemo(() => GUIDE_STEPS[activeStep], [activeStep]);

  useEffect(() => {
    pushToDataLayer("guide_viewed", { guide_type: "how_to_send_money" });
  }, []);
  
  return (
    <div className="max-w-5xl mx-auto p-6">
      <BreadCrumb
        title="Ultimate Guide to Sending Money Internationally: Smart Strategies for Seamless Transfers"
        desc="International money transfers are more than just moving funds across borders. They represent a critical financial lifeline for millions—connecting families, supporting global businesses, and enabling personal and professional opportunities. However, the process involves multiple layers of complexity that can impact your transfer's cost, speed, and reliability."
      />

      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-1/3">
          <div className="sticky top-4 bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-lg font-semibold mb-4">Steps</h2>
            <div className="space-y-2">
              {GUIDE_STEPS.map(({ title }, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                    activeStep === index
                      ? "bg-blue-50 text-[#7b3BCC]"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <span className="font-medium">
                    {index + 1}. {title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="md:w-2/3">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-blue-100 text-[#7b3BCC] rounded-full text-sm font-medium">
                Step {activeStep + 1} of {GUIDE_STEPS.length}
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-4">{title}</h2>
            <p className="text-gray-700 leading-relaxed">{content}</p>
            {bullets && (
              <div className="flex flex-col gap-2 font-semibold mt-2">
                {bullets?.map((item) => (
                  <div className="flex">
                    <FaCircle className="text-[14px] mt-1 mr-2" /> <p>{item}</p>
                  </div>
                ))}
              </div>
            )}
            {bank_transfer && (
              <div className="flex flex-col gap-2  mt-3">
                <p className="font-semibold">{bank_transfer?.title}: </p>
                {bank_transfer?.bullets?.map((item) => (
                  <div className="flex">
                    <FaCircle className="text-[14px] mt-1 mr-2" /> <p>{item}</p>
                  </div>
                ))}
              </div>
            )}
            {credit_cards && (
              <div className="flex flex-col gap-2  mt-3">
                <p className="font-semibold ">{credit_cards?.title}:</p>
                {credit_cards?.bullets?.map((item) => (
                  <div className="flex">
                    <FaCircle className="text-[14px] mt-1 mr-2" /> <p>{item}</p>
                  </div>
                ))}
              </div>
            )}

            {digital_wallets && (
              <div className="flex flex-col gap-2  mt-3">
                <p className="font-semibold">{digital_wallets?.title}:</p>
                {digital_wallets?.bullets?.map((item) => (
                  <div className="flex">
                    <FaCircle className="text-[14px] mt-1 mr-2" /> <p>{item}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setActiveStep((prev) => prev - 1)}
                disabled={activeStep === 0}
                className={`px-4 py-2 rounded-lg ${
                  activeStep === 0
                    ? "bg-gray-100 text-gray-400 font-[600] cursor-not-allowed"
                    : "bg-gray-400 text-gray-700 font-[600] hover:bg-gray-200"
                }`}
              >
                Previous Step
              </button>
              <button
                onClick={() => setActiveStep((prev) => prev + 1)}
                disabled={activeStep === GUIDE_STEPS.length - 1}
                className={`px-4 py-2 rounded-lg ${
                  activeStep === GUIDE_STEPS.length - 1
                    ? "bg-gray-100 text-gray-400 font-[600] cursor-not-allowed"
                    : "bg-[#7b3BCC] text-white font-[600] hover:bg-[#6830b1]"
                }`}
              >
                Next Step
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoneyTransferGuide;
